import {
  CARE_SPONSORSHIP_CAP_GATED_APPOINTMENT_TYPES_BY_SPONSOR_TYPE,
  CARE_SPONSORSHIP_ELIGIBLE_APPOINTMENT_TYPES_BY_SPONSOR_TYPE,
  CARE_SPONSORSHIP_INTAKE_CAP_GATING_CUTOVER_DATE,
  MARBLE_CARE_SPONSOR_TYPES,
  SCHOOL_CARE_SPONSOR_TYPES,
  SCHOOL_CARE_SPONSORSHIP_SUPERVISEE_DEFAULT_STATES,
} from "@marble/core/modules/care-sponsorship/constants";
import type { CareSponsorshipSuperviseeRestriction } from "@marble/core/modules/care-sponsorship/types";
import { AppointmentType, CareSponsorType, State } from "@prisma/client";

/**
 * THE single function that decides which appointment types actually consume
 * a SPECIFIC sponsorship's numeric session-cap allotment — the only place in
 * the codebase that knows about the INTAKE cutover. Every other cap-gating
 * helper/check in this file (and every caller elsewhere) is defined in terms
 * of this one, so there is exactly one place to look, and exactly one place
 * that could get the INTAKE rule wrong.
 *
 * Starts from `CARE_SPONSORSHIP_CAP_GATED_APPOINTMENT_TYPES_BY_SPONSOR_TYPE`
 * (which already includes INTAKE for the Marble sponsor types — see its doc
 * comment) and strips INTAKE back out when this sponsorship started before
 * `CARE_SPONSORSHIP_INTAKE_CAP_GATING_CUTOVER_DATE` (see that constant's doc
 * comment for why).
 *
 * `sponsorshipStartedAt` accepts `null`/`undefined` — treated as pre-cutover,
 * i.e. INTAKE never counts — as a defensive fallback for callers that don't
 * have the real sponsorship at hand (`CareSponsorship.startedAt` is a
 * required, defaulted DB column, so a real sponsorship always has one; only
 * a caller working off a partial/unresolved sponsor type would hit this).
 */
export const getCareSponsorshipCapGatedAppointmentTypes = (
  careSponsorType: CareSponsorType,
  sponsorshipStartedAt: Date | null | undefined,
): readonly AppointmentType[] => {
  const base =
    CARE_SPONSORSHIP_CAP_GATED_APPOINTMENT_TYPES_BY_SPONSOR_TYPE[
      careSponsorType
    ];
  // `base` should always be defined in production (careSponsorType is a
  // required Prisma enum column) — the null guard just mirrors how a plain
  // Record lookup would behave for an unresolved/malformed key, rather than
  // throwing, for any caller that hasn't fully resolved a CareSponsorType.
  if (
    base == null ||
    isIntakeCapGatedForCareSponsorship(careSponsorType, sponsorshipStartedAt)
  ) {
    return base;
  }
  // Pre-cutover (or non-Marble, where INTAKE was never in `base` anyway) —
  // strip INTAKE back out.
  return base.filter((type) => type !== AppointmentType.INTAKE);
};

/**
 * Whether INTAKE counts toward THIS SPECIFIC sponsorship's numeric session
 * cap — a convenience boolean over `getCareSponsorshipCapGatedAppointmentTypes`
 * for copy/notification code that wants a plain yes/no rather than checking
 * list membership. True only for the Marble sponsor types (see
 * `isMarbleCareSponsorship`) AND only when the sponsorship started on/after
 * `CARE_SPONSORSHIP_INTAKE_CAP_GATING_CUTOVER_DATE`.
 */
export const isIntakeCapGatedForCareSponsorship = (
  careSponsorType: CareSponsorType,
  sponsorshipStartedAt: Date | null | undefined,
): boolean =>
  isMarbleCareSponsorship(careSponsorType) &&
  sponsorshipStartedAt != null &&
  sponsorshipStartedAt.getTime() >=
    CARE_SPONSORSHIP_INTAKE_CAP_GATING_CUTOVER_DATE.getTime();

/**
 * Whether `appointmentType` consumes THIS `careSponsorType`'s numeric
 * session-cap allotment — a thin wrapper over
 * `getCareSponsorshipCapGatedAppointmentTypes` (see its doc comment; that's
 * where the INTAKE-cutover logic actually lives, not here).
 *
 * `sponsorshipStartedAt` is required (not defaulted) so a caller can't
 * accidentally get the "assume pre-cutover" answer by simply forgetting the
 * argument — pass the real sponsorship's `startedAt` whenever you have one;
 * pass `null` explicitly (with a comment saying why) for the rare caller
 * that's checking a type unaffected by the cutover
 * (INDIVIDUAL_THERAPY/MEDICATION_MANAGEMENT/MEDICATION_MANAGEMENT_INTAKE/
 * FAMILY_THERAPY) or that genuinely has no sponsorship to resolve one from.
 *
 * Shared by session-usage computation (`getCareSponsorshipSessionUsage`) and
 * appointment-type eligibility
 * (`isCareSponsorshipEligibleAppointmentTypeForSponsorType`) so they can't
 * drift apart. See
 * `CARE_SPONSORSHIP_CAP_GATED_APPOINTMENT_TYPES_BY_SPONSOR_TYPE`'s doc
 * comment for which types count for which sponsor type.
 */
export const isCareSponsorshipCapGatedAppointmentType = (
  appointmentType: AppointmentType,
  careSponsorType: CareSponsorType,
  sponsorshipStartedAt: Date | null | undefined,
): boolean =>
  getCareSponsorshipCapGatedAppointmentTypes(
    careSponsorType,
    sponsorshipStartedAt,
  ).includes(appointmentType);

/**
 * Whether `appointmentType` is covered by care sponsorship at all for THIS
 * specific `careSponsorType` — see
 * `CARE_SPONSORSHIP_ELIGIBLE_APPOINTMENT_TYPES_BY_SPONSOR_TYPE`'s doc
 * comment for which types are covered for which sponsor type. A covered type
 * either consumes that sponsor type's session cap
 * (`isCareSponsorshipCapGatedAppointmentType`) or doesn't (GUARDIAN_CONSULT
 * has its own separate one-consult cap; INTAKE has no cap for
 * SCHOOL/SCHOOL_NETWORK, and for the Marble sponsor types it depends on the
 * sponsorship's start date — see `isIntakeCapGatedForCareSponsorship`).
 *
 * Always resolve the active sponsorship's `CareSponsorType` first and call
 * this with it — there's deliberately no sponsor-type-agnostic shortcut, so
 * a type that's eligible for one sponsor type (e.g. MEDICATION_MANAGEMENT
 * for INSURANCE_PAYER_NETWORK_STATUS) can't leak into another (SCHOOL/
 * SCHOOL_NETWORK) that doesn't cover it.
 */
export const isCareSponsorshipEligibleAppointmentTypeForSponsorType = (
  appointmentType: AppointmentType,
  careSponsorType: CareSponsorType,
): boolean =>
  CARE_SPONSORSHIP_ELIGIBLE_APPOINTMENT_TYPES_BY_SPONSOR_TYPE[
    careSponsorType
  ].includes(appointmentType);

/**
 * SCHOOL/SCHOOL_NETWORK sponsorships are a family/school-facing program —
 * a school (or network) is paying for the student's care directly.
 * INSURANCE_PAYER_NETWORK_STATUS sponsorships are an internal billing
 * arrangement instead (Marble still bills the payer as if INN), not a
 * program to announce to school workers, admins, or therapists. Use this
 * to gate any UI/messaging that's specifically about the school-sponsor
 * program — e.g. `CareSponsorshipEnrolledBanner`.
 */
export const isSchoolCareSponsorType = (
  careSponsorType: CareSponsorType,
): boolean => SCHOOL_CARE_SPONSOR_TYPES.includes(careSponsorType);

/**
 * INSURANCE_PAYER_NETWORK_STATUS and MARBLE_STATE_UNINSURED_PATIENTS
 * sponsorships are both Marble billing/covering care directly (the payer
 * relationship or the state program, never a school), as opposed to
 * SCHOOL/SCHOOL_NETWORK where a school is paying — see
 * `isSchoolCareSponsorType`'s docstring for that side. Both cover the wider
 * appointment-type set (MEDICATION_MANAGEMENT/FAMILY_THERAPY in addition to
 * INDIVIDUAL_THERAPY — see `CARE_SPONSORSHIP_ELIGIBLE_APPOINTMENT_TYPES_BY_SPONSOR_TYPE`),
 * so copy/logic that varies on "is this a school sponsorship or a Marble
 * one" should check this instead of hardcoding
 * `=== CareSponsorType.INSURANCE_PAYER_NETWORK_STATUS`, which would silently
 * miss the state-uninsured type.
 */
export const isMarbleCareSponsorship = (
  careSponsorType: CareSponsorType,
): boolean => MARBLE_CARE_SPONSOR_TYPES.includes(careSponsorType);

/**
 * Whether scheduling a sponsor-covered session should limit the therapist
 * pool to supervisees (LMSW-level), and whether admins can override it.
 * Only meaningful when the sponsorship actually covers the appointment
 * (`canCareSponsorshipApplyToAppointment`): that is the path where no
 * insurance is billed and the sponsor pays for the session.
 *
 * - Marble sponsorships (`isMarbleCareSponsorship`) are `REQUIRED`: Marble
 *   pays, so it is always supervisees.
 * - School/school-network sponsorships are `DEFAULT` for students living in
 *   `SCHOOL_CARE_SPONSORSHIP_SUPERVISEE_DEFAULT_STATES`, so ops can still
 *   book an independently licensed therapist from admin booking when needed.
 * - Anything else is `null` (no restriction).
 */
export const getCareSponsorshipSuperviseeRestriction = (
  careSponsorType: CareSponsorType,
  clientState: State | null | undefined,
): CareSponsorshipSuperviseeRestriction | null => {
  if (isMarbleCareSponsorship(careSponsorType)) {
    return "REQUIRED";
  }
  if (
    isSchoolCareSponsorType(careSponsorType) &&
    clientState != null &&
    SCHOOL_CARE_SPONSORSHIP_SUPERVISEE_DEFAULT_STATES.includes(clientState)
  ) {
    return "DEFAULT";
  }
  return null;
};

/**
 * Whether eligible INN insurance should auto-end this sponsorship so billing
 * can switch to the plan on file (`SWITCHED_TO_INSURANCE`).
 *
 * School / school-network and state-uninsured programs are fallbacks for
 * students we cannot bill — true billable INN should end them.
 * `INSURANCE_PAYER_NETWORK_STATUS` is the opposite: those students are
 * supposed to look INN while Marble bridges the payer. Ending on INN+eligible
 * would bounce them off the sponsor and let `upsertInsuranceInfo` re-enroll.
 */
export const shouldEndCareSponsorshipWhenInsuranceBecomesBillableInn = (
  careSponsorType: CareSponsorType,
): boolean =>
  isSchoolCareSponsorType(careSponsorType) ||
  careSponsorType === CareSponsorType.MARBLE_STATE_UNINSURED_PATIENTS;

/**
 * Whether this sponsor type's numeric cap is shared across more than just
 * INDIVIDUAL_THERAPY (i.e. it also cap-gates FAMILY_THERAPY /
 * MEDICATION_MANAGEMENT / MEDICATION_MANAGEMENT_INTAKE — see
 * `CARE_SPONSORSHIP_CAP_GATED_APPOINTMENT_TYPES_BY_SPONSOR_TYPE`). Copy that
 * needs to say "individual therapy sessions" vs. just "therapy sessions"
 * should derive it from that config directly via this helper, rather than
 * hardcoding which sponsor types happen to share the wider cap today.
 *
 * Goes through `getCareSponsorshipCapGatedAppointmentTypes` (passing `null`
 * for `sponsorshipStartedAt`, i.e. pre-cutover) rather than reading the
 * Record directly — the answer here never actually depends on whether
 * INTAKE is in the list (MEDICATION_MANAGEMENT/FAMILY_THERAPY are already
 * enough to make this true for the Marble sponsor types, and it's always
 * false for SCHOOL/SCHOOL_NETWORK, which never gets INTAKE either way), but
 * routing through the shared function keeps this file's only direct Record
 * read inside `getCareSponsorshipCapGatedAppointmentTypes`.
 */
export const careSponsorshipCapSpansMultipleTherapyTypes = (
  careSponsorType: CareSponsorType,
): boolean =>
  getCareSponsorshipCapGatedAppointmentTypes(careSponsorType, null).some(
    (appointmentType) => appointmentType !== AppointmentType.INDIVIDUAL_THERAPY,
  );
